import { captureException } from '@sentry/react'
import { getAIVideoProjects_API } from 'api'
import { PROJECT_MEDIA_TYPES } from 'consts'
import aiAvatarSlice from 'store/features/aiAvatar/slice'
import { avatarVoiceInitial } from 'store/features/aiAvatar/initialState'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import {
  clampQualityForFreeUser,
  resolveAvatarReferenceDimensions
} from 'store/features/aiAvatar/utils'
import {
  clearAssetPendingByImageId,
  getAssetOp,
  removePendingSourceImageEntry,
  storeAssetResultByImageId
} from 'store/features/aiAvatar/sourceImageStorage'
import { reconcilePendingSourceImage } from 'store/features/aiAvatar/thunks/sourceImage'
import { cacheSelectors } from 'store/features/cache/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { TAppDispatch, TGetState, TRootState } from 'store/types'
import {
  AvatarSource,
  AvatarVoiceTab,
  IAvatarGenerationParams,
  IClonedVoice,
  IPreviousSourceImage
} from 'store/types/aiAvatar'
import { withAppThunks } from 'store/with-app-thunks'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { IAIVideoProject } from 'types/aiVideo'
import { IDimensions } from 'types'

const { actions } = aiAvatarSlice

const getAIAvatarByAssetId = (assetId: number) =>
  withAppThunks(
    appThunks =>
      async (
        dispatch: TAppDispatch,
        getState: TGetState
      ): Promise<IAIVideoProject | null> => {
        const state = getState()
        const aiAvatarCache = cacheSelectors.aiAvatarCache(state)
        const existingItem = aiAvatarCache[assetId]

        if (existingItem) {
          return existingItem
        }

        try {
          const { data } = await getAIVideoProjects_API({
            projectIds: [assetId]
          })

          if (data.length === 0) {
            return null
          }

          dispatch(appThunks.cacheThunks.addToAiAvatarCache(data))

          return data[0]
        } catch (error) {
          if (!(error instanceof ApiError)) {
            captureException(new ReduxThunkError(error))
          }
          return null
        }
      }
  )

const setGenerationParams =
  (params: IAvatarGenerationParams) => (dispatch: TAppDispatch) => {
    // Replacing the whole form abandons any in-flight source-image op (the
    // reducer clears the correlation) — drop the persisted case-1 entry too,
    // or a reload would resurrect the abandoned op.
    removePendingSourceImageEntry()
    dispatch(actions.setGenerationParams(params))
  }

// Maps an avatar (ai-video) project's saved data into generate-form params.
// Returns null when the project has no voiceover audio (the speech/voice source
// the form needs). `targetMediaItemId` ties the params to a timeline clip for an
// in-place regen, or is null for a fresh generation prefilled from history.
const buildAvatarGenerationParams = ({
  data,
  state,
  targetMediaItemId,
  imageDimensions
}: {
  data: IAIVideoProject
  state: TRootState
  targetMediaItemId: string | null
  // The reference image's real pixel size (resolveAvatarReferenceDimensions),
  // carried onto the avatar so a regeneration FITs the loading placeholder to
  // the photo's own aspect ratio instead of stretching it to the canvas ratio.
  // Undefined for public avatars / when it couldn't be resolved — then the
  // aspect-ratio fake rect is used, which is correct for those.
  imageDimensions?: IDimensions
}): IAvatarGenerationParams | null => {
  const audios = data.referenceMedias.audios

  if (!audios) {
    return null
  }
  const { text, speakerId, langCode } = audios[0]

  return {
    avatar: {
      // placeholder — only read to highlight a public preset
      id: String(data.id),
      url: data.imageUrl!,
      // non-PUBLIC so the preset-highlight is skipped; url drives everything
      source: AvatarSource.MY_FILES,
      // Real image size so the regen loader FITs (keeps proportions) instead of
      // filling — see `resolveAvatarMediaDimensions`.
      width: imageDimensions?.width,
      height: imageDimensions?.height
    },
    // A voice-clone generation has no catalog speaker (`speakerId` is null;
    // `refAudio` carries the clone — restored separately, see
    // `buildRestoredClonedVoice`) — keep the default library voice then, so the
    // Voice library sub-tab stays usable if the user detaches the clone.
    voice: {
      ...avatarVoiceInitial,
      langCode: langCode ?? avatarVoiceInitial.langCode,
      speakerId: speakerId ?? avatarVoiceInitial.speakerId
    },
    // A free user can select a clip rendered at a premium quality (made
    // while subscribed, or from a template/duplicate); clamp before it lands
    // in the form so the dropdown shows a value they can actually use. The
    // generate boundary (`requestAvatarGeneration`) clamps again as a guard.
    quality: clampQualityForFreeUser(
      data.quality,
      aiAvatarSelectors.avatarPossibleQualities(state),
      userSelectors.isFreeUser(state)
    ),
    speechText: text,
    // Pre-fill the aspect-ratio toggle with the clip's original ratio so a
    // regeneration keeps it (null falls back to the project ratio).
    aspectRatio: data.aspectRatio,
    targetMediaItemId
  }
}

// The display name for a restored clone card: the audio file's name from its
// URL. The original My Files title isn't in the avatar-project response, so
// the filename is the best available label.
const cloneNameFromUrl = (url: string): string => {
  try {
    const fileName = new URL(url).pathname.split('/').pop() ?? ''
    return decodeURIComponent(fileName) || url
  } catch {
    return url
  }
}

// Maps a clone-generated avatar project's `refAudio` back into a cloned-voice
// attachment, or null for catalog-speaker generations. The file lives in
// My Files → Sounds (that's where clone uploads are stored), hence MY_FILES.
const buildRestoredClonedVoice = (
  data: IAIVideoProject
): IClonedVoice | null => {
  const audio = data.referenceMedias.audios?.[0]

  if (!audio?.refAudio) {
    return null
  }

  return {
    // The reference-audio record id — the My Files sound id isn't in the
    // response. Nothing consumes the id today; the card shows name/url only.
    id: audio.id,
    name: cloneNameFromUrl(audio.refAudio),
    url: audio.refAudio,
    source: AvatarSource.MY_FILES
  }
}

// Re-attaches the cloned voice a project was generated with, after a form load
// wiped `voiceClone` (the `setGenerationParams` reducer resets it so a stale
// clone can't leak into a speaker-generated clip's form). Switching to the
// Clone sub-tab makes `activeRefAudio` pick it up again, so the trial preview
// and (re)generate send the clip's original `refAudio` instead of the default
// `speakerId` — and the UI shows exactly that.
//
// The clone comes from `generationClones` — the clone remembered at this
// session's generate kickoff, matched via the project's
// `meta.aiAvatarProjectId` (it carries the real My Files name/id). The
// `refAudio` echoed in the projects response is only a fallback for
// generations made in a previous session; it isn't reliably present.
const restoreClonedVoice =
  (data: IAIVideoProject) => (dispatch: TAppDispatch, getState: TGetState) => {
    const avatarProjectId = data.meta?.aiAvatarProjectId
    const rememberedClone = avatarProjectId
      ? aiAvatarSelectors.generationClones(getState())[avatarProjectId]
      : undefined
    const clonedVoice = rememberedClone ?? buildRestoredClonedVoice(data)

    if (!clonedVoice) {
      return
    }

    dispatch(actions.setClonedVoice(clonedVoice))
    dispatch(actions.setVoiceCloneTab(AvatarVoiceTab.CLONE))
  }

const setRegenerationForm =
  (mediaItemId: string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    try {
      const mediaItems = projectDataSelectors.mediaItems(state)
      const targetMedia = mediaItems.find(
        item =>
          item.id === mediaItemId && item.type === PROJECT_MEDIA_TYPES.aiAvatar
      )
      if (!targetMedia) {
        return
      }

      // `dispatch` of a `withAppThunks` thunk doesn't carry the return type, so
      // a cast is unavoidable — but cast to the ACCURATE nullable type and then
      // narrow. `getAIAvatarByAssetId` returns null when the project isn't found
      // or the fetch fails; bail instead of dereferencing (the old
      // `as IAIVideoProject` cast hid the null and threw on `.referenceMedias`,
      // silently breaking the regen form).
      const data = (await dispatch(
        getAIAvatarByAssetId(Number(targetMedia.assetId))
      )) as IAIVideoProject | null

      if (!data) {
        return
      }

      // Recover the reference image's real dimensions so the regen loader FITs
      // (keeps proportions) instead of filling the canvas (the "fill" bug).
      const imageDimensions = await resolveAvatarReferenceDimensions(data)

      const params = buildAvatarGenerationParams({
        data,
        state,
        targetMediaItemId: mediaItemId,
        imageDimensions
      })

      if (!params) {
        return
      }

      // Persisted Enhance/Resize state for this clip (the case-2 ops map): the
      // backend project still holds the ORIGINAL reference image, so a stored
      // result must override it — and the backend originals, snapshotted
      // BEFORE the override, arm the one-step Undo so it survives
      // reselect/reload.
      const assetOp = getAssetOp(targetMedia.assetId)
      const storedResult = assetOp?.result
      let originalSnapshot: IPreviousSourceImage | null = null
      if (storedResult && params.avatar) {
        originalSnapshot = {
          avatar: params.avatar,
          aspectRatio: params.aspectRatio
        }
        params.avatar = {
          ...params.avatar,
          url: storedResult.url,
          width: storedResult.width,
          height: storedResult.height
        }
        if (storedResult.aspectRatio) {
          params.aspectRatio = storedResult.aspectRatio
        }
      }

      dispatch(setGenerationParams(params))
      // After the params load (which resets `voiceClone`) — a clone-generated
      // clip gets its cloned voice back so regenerating re-sends its refAudio.
      dispatch(restoreClonedVoice(data))
      // AFTER `setGenerationParams` — that reducer clears the snapshot.
      if (originalSnapshot) {
        dispatch(actions.setPreviousSourceImage(originalSnapshot))
      }

      // A still-in-flight op for this clip: re-arm the slot + loader (the
      // `setGenerationParams` reducer just cleared them — by design, but this
      // form IS the op's form) and reconcile, since the completion may have
      // fired while no form was listening (INV-AIMG-1).
      const pendingOp = assetOp?.pending
      if (pendingOp) {
        dispatch(
          actions.setPendingSourceImage({
            id: pendingOp.imageId,
            aspectRatio: pendingOp.targetAspectRatio
          })
        )
        dispatch(actions.setAvatarLoading(true))
        dispatch(
          reconcilePendingSourceImage(pendingOp, {
            onApplied: result =>
              storeAssetResultByImageId(pendingOp.imageId, result),
            onExpired: () => clearAssetPendingByImageId(pendingOp.imageId)
          })
        )
      }
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

// Prefills the generate form from an avatar (ai-video) asset id directly, with
// no timeline clip required — used by the AI History "Re-Generate" action, where
// the chosen clip generally isn't on the timeline. `targetMediaItemId` is null so
// a Generate click produces a fresh clip (mirrors aiVideo's same-named thunk).
const setGenerationParamsByAssetId =
  (assetId: number | string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const numericAssetId = Number(assetId)
    if (isNaN(numericAssetId)) {
      return
    }
    try {
      const data = (await dispatch(
        getAIAvatarByAssetId(numericAssetId)
      )) as IAIVideoProject | null

      if (!data) {
        return
      }

      // Same reference-image FIT recovery as `setRegenerationForm`.
      const imageDimensions = await resolveAvatarReferenceDimensions(data)

      const params = buildAvatarGenerationParams({
        data,
        state: getState(),
        targetMediaItemId: null,
        imageDimensions
      })

      if (!params) {
        return
      }

      // Prefer this avatar project's persisted Enhance/Resize result (case-2
      // ops map — keyed by the same project id): a history prefill should show
      // the refined image too. Pending ops are not re-armed here — this form
      // has no target clip to route a completion back to.
      const storedResult = getAssetOp(String(numericAssetId))?.result
      if (storedResult && params.avatar) {
        params.avatar = {
          ...params.avatar,
          url: storedResult.url,
          width: storedResult.width,
          height: storedResult.height
        }
        if (storedResult.aspectRatio) {
          params.aspectRatio = storedResult.aspectRatio
        }
      }

      dispatch(setGenerationParams(params))
      // Same restore as `setRegenerationForm`: a history prefill of a
      // clone-generated project re-attaches its cloned voice.
      dispatch(restoreClonedVoice(data))
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

export const formThunks = {
  getAIAvatarByAssetId,
  setRegenerationForm,
  setGenerationParamsByAssetId,
  setGenerationParams
}
